import { motion } from "framer-motion";
import { MapPin, Phone, Clock, Instagram, Mail } from "lucide-react";

const doctorEmails = [
  { name: "Dr Mirabelle Tournoud", email: "docteur.tournoud@gmail.com", id: "tournoud" },
  { name: "Dr Jeremy Vermot", email: "dr.vermotjeremy@gmail.com", id: "vermot" },
  { name: "Dr Léo Apelbaum", email: "drapelbauleo@gmail.com", id: "apelbaum" },
];

export const Contact = () => {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 md:py-32 bg-[#2D241E] text-[#FAF8F5] relative overflow-hidden"
    >
      {/* Background clinic photo */}
      <div className="absolute inset-0 opacity-20">
        <img
          src="https://customer-assets.emergentagent.com/job_smile-lagavotte/artifacts/xmslzbda_PSTK4783.JPG"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2D241E] via-[#2D241E]/85 to-[#2D241E]/40" />
      </div>
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[#BE6C42]/15 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-[#BE6C42]/10 blur-3xl pointer-events-none" />

      <div className="container-xl relative">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className="overline !text-[#E4A57E]">Contact</span>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05]">
              Un sourire vous attend
              <br />
              <span className="italic text-[#E4A57E] font-light">
                à la Gavotte.
              </span>
            </h2>
            <p className="mt-6 text-[#E8DED5] text-base sm:text-lg font-light leading-relaxed max-w-lg">
              Notre équipe vous accueille dans un cadre chaleureux, pensé pour
              votre confort. N&apos;hésitez pas à nous contacter ou à prendre
              rendez-vous directement en ligne.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="space-y-2"
          >
            <div className="border-t border-[#FAF8F5]/15 py-6 flex items-start gap-5">
              <div className="shrink-0 w-11 h-11 rounded-full bg-[#BE6C42]/20 flex items-center justify-center">
                <MapPin size={18} className="text-[#E4A57E]" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#E4A57E]/80 mb-1">
                  Adresse
                </div>
                <a
                  href="https://maps.google.com/?q=15+bis+avenue+François+Mitterand+13170+Les+Pennes-Mirabeau"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-serif text-xl hover:text-[#E4A57E] transition-colors"
                  data-testid="contact-address"
                >
                  15 bis avenue François Mitterand
                  <br />
                  13170 Les Pennes-Mirabeau
                </a>
              </div>
            </div>

            <div className="border-t border-[#FAF8F5]/15 py-6 flex items-start gap-5">
              <div className="shrink-0 w-11 h-11 rounded-full bg-[#BE6C42]/20 flex items-center justify-center">
                <Phone size={18} className="text-[#E4A57E]" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#E4A57E]/80 mb-1">
                  Téléphone
                </div>
                <a
                  href="tel:0491515509"
                  className="font-serif text-xl hover:text-[#E4A57E] transition-colors"
                  data-testid="contact-phone"
                >
                  04 91 51 55 09
                </a>
              </div>
            </div>

            <div className="border-t border-[#FAF8F5]/15 py-6 flex items-start gap-5">
              <div className="shrink-0 w-11 h-11 rounded-full bg-[#BE6C42]/20 flex items-center justify-center">
                <Clock size={18} className="text-[#E4A57E]" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#E4A57E]/80 mb-1">
                  Horaires
                </div>
                <div className="font-serif text-xl leading-snug">
                  Lundi — Vendredi
                  <br />
                  <span className="text-[#E8DED5]/80 text-base font-sans font-light">
                    Sur rendez-vous
                  </span>
                </div>
              </div>
            </div>

            <div className="border-t border-[#FAF8F5]/15 py-6 flex items-start gap-5">
              <div className="shrink-0 w-11 h-11 rounded-full bg-[#BE6C42]/20 flex items-center justify-center">
                <Mail size={18} className="text-[#E4A57E]" />
              </div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-[0.2em] text-[#E4A57E]/80 mb-3">
                  Emails des praticiens
                </div>
                <div className="space-y-2.5">
                  {doctorEmails.map((d) => (
                    <div key={d.id}>
                      <div className="text-[11px] text-[#E8DED5]/70 tracking-wide">
                        {d.name}
                      </div>
                      <a
                        href={`mailto:${d.email}`}
                        className="font-serif text-lg hover:text-[#E4A57E] transition-colors break-all"
                        data-testid={`contact-email-${d.id}`}
                      >
                        {d.email}
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="border-t border-b border-[#FAF8F5]/15 py-6 flex items-start gap-5">
              <div className="shrink-0 w-11 h-11 rounded-full bg-[#BE6C42]/20 flex items-center justify-center">
                <Instagram size={18} className="text-[#E4A57E]" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#E4A57E]/80 mb-1">
                  Instagram
                </div>
                <a
                  href="https://www.instagram.com/cabinetdentairelagavotte/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-serif text-xl hover:text-[#E4A57E] transition-colors"
                  data-testid="contact-instagram"
                >
                  @cabinetdentairelagavotte
                </a>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Instagram QR card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mt-16 grid md:grid-cols-12 gap-10 items-center bg-[#FAF8F5] text-[#2D241E] rounded-3xl p-6 md:p-10 border border-[#E8DED5]"
          data-testid="instagram-qr-card"
        >
          <div className="md:col-span-5">
            <a
              href="https://www.instagram.com/cabinetdentairelagavotte/"
              target="_blank"
              rel="noopener noreferrer"
              className="block group relative overflow-hidden rounded-2xl"
              data-testid="instagram-qr-link"
            >
              <img
                src="/downloads/affiche-instagram-la-gavotte.png"
                alt="Scannez le QR code pour suivre le Cabinet Dentaire La Gavotte sur Instagram"
                className="w-full h-auto object-contain transition-transform duration-700 group-hover:scale-[1.02]"
              />
            </a>
          </div>
          <div className="md:col-span-7">
            <span className="overline">Suivez-nous</span>
            <h3 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[1.05]">
              Et si vous{" "}
              <span className="italic text-[#BE6C42] font-light">
                nous suiviez ?
              </span>
            </h3>
            <p className="mt-6 body-text text-base sm:text-lg">
              Retrouvez nos conseils et l&apos;actualité du cabinet directement
              sur Instagram. Scannez simplement le QR code avec votre
              téléphone, ou cliquez sur le bouton ci-dessous.
            </p>
            <a
              href="https://www.instagram.com/cabinetdentairelagavotte/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary mt-8"
              data-testid="instagram-follow-cta"
            >
              <Instagram size={18} />
              @cabinetdentairelagavotte
            </a>
          </div>
        </motion.div>

        {/* Map */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="mt-16 rounded-3xl overflow-hidden border border-[#FAF8F5]/15 shadow-[0_20px_60px_rgba(0,0,0,0.3)]"
          data-testid="contact-map"
        >
          <iframe
            title="Localisation du Cabinet Dentaire La Gavotte"
            src="https://www.google.com/maps?q=15+bis+avenue+Fran%C3%A7ois+Mitterand+13170+Les+Pennes-Mirabeau&output=embed"
            width="100%"
            height="420"
            style={{ border: 0, display: "block", filter: "saturate(0.85) contrast(1.05)" }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
